import { Outlet } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { NotificationArea } from '@aeris/ui'

import { usePcMode } from '../lib/desktop.ts'
import { useShellFrame } from '../lib/shell-frame.ts'

import { AlertCenter } from './alert-center.tsx'
import { AppShell, ModuleWindowMain } from './app-shell.tsx'
import { BrandingProvider } from './branding-provider.tsx'
import { DesktopShell } from './desktop/desktop-shell.tsx'
import { WindowMain } from './desktop/window-main.tsx'
import { DesktopUiUpdate } from './desktop-ui-update.tsx'
import { GlobalScan } from './global-scan.tsx'
import { PageGate } from './page-gate.tsx'
import { RequireSession } from './require-session.tsx'
import { StoreOrderAlert } from './store-order-alert.tsx'
import { SupportBanner } from './support-banner.tsx'

/**
 * A moldura em volta de toda tela de dentro do shell.
 *
 * Quem decide qual é o contexto `ShellFrame` (F138), e não a URL global: no
 * Modo PC várias telas convivem na mesma página, cada uma com a navegação
 * dela, e a barra de endereço deixa de saber qual moldura cada árvore tem.
 *
 * Num arquivo próprio, e não em `router.tsx`, porque o roteador exporta a
 * fábrica `createAppRouter` — e um módulo que exporta componente e função ao
 * mesmo tempo não é uma fronteira de Fast Refresh.
 */
export const ShellLayout = (): ReactNode => {
  const frame = useShellFrame()
  const pcMode = usePcMode()

  /*
   * Uma janela da área de trabalho: só a tela. Sem barra lateral, e sem conferir
   * a sessão de novo — a área de trabalho em volta já confere, e conferida por
   * janela um "sair" desenharia a tela de login dentro de cada uma.
   */
  if (frame.kind === 'window') return <WindowMain />

  return (
    <RequireSession>
      {/*
       * A cor da loja (F169), ligada uma vez para as três molduras — e para os
       * diálogos, que o Radix desenha num portal FORA desta árvore. É por isso
       * que ela escreve no `<html>` em vez de num `<style>` local.
       */}
      <BrandingProvider>
        {/*
         * O canto dos avisos (F248): o toast, o pedido da loja e o produto que
         * o leitor achou, empilhados num lugar só. No Modo PC, acima da barra
         * de tarefas.
         */}
        <NotificationArea above={pcMode ? 'taskbar' : 'edge'}>
          {/*
           * Um lugar só para a faixa de suporte (F169), acima das três molduras.
           *
           * Montada aqui e não dentro de cada uma porque ela vale para as três, e
           * porque uma faixa que existe em duas das três é pior que nenhuma: o
           * operador aprenderia que a ausência dela significa "não estou em
           * suporte", e um dia estaria.
           */}
          <SupportBanner />
          {/* A tela nova do desktop, baixada do servidor (F213). */}
          <DesktopUiUpdate />
          {/*
           * O leitor de código de barras em qualquer tela (F206): um só, acima
           * das três molduras, como a faixa de suporte.
           */}
          <GlobalScan />
          {/* O Pix pago avisa o caixa em qualquer tela (F205). */}
          <AlertCenter />
          {/*
           * A module window is one screen, so it gets no sidebar and no topbar.
           *
           * The ROUTE is the same one the browser renders — Master Specification
           * section 26 forbids a second implementation, and there is not one here;
           * only the chrome around it differs. Two navigation bars inside a window
           * the size of a form is how a desktop ERP starts feeling like a browser
           * with extra steps.
           */}
          {/*
           * `h-[100dvh]`, igual ao shell, e não `min-h-`.
           *
           * Com `min-h-`, quem rolava era o DOCUMENTO; no shell, quem rola é o
           * `<main>` de altura fixa. A MESMA tela tinha dois comportamentos de
           * rolagem conforme onde foi aberta — janela do Modo PC ou navegador —, e
           * qualquer coisa presa por `sticky` (o cabeçalho de tabela, a prévia do
           * modelo) grudava em elementos diferentes nos dois casos. Uma tela só,
           * um modelo de rolagem só.
           */}
          {frame.kind === 'native-module' ? (
            <ModuleWindowMain />
          ) : pcMode ? (
            <DesktopShell />
          ) : (
            <AppShell>
              <PageGate>
                <Outlet />
              </PageGate>
              {/*
               * O pedido da loja chegou: a vendedora vê em qualquer tela (F224).
               * Dentro da moldura (F248) — no Modo PC ele mora na área de
               * trabalho, para "Abrir" abrir a janela.
               */}
              <StoreOrderAlert />
            </AppShell>
          )}
        </NotificationArea>
      </BrandingProvider>
    </RequireSession>
  )
}
